import { columnRef } from './columnRef.js';
import { lineIndent, objectKey } from './valueBodyTemplates.js';

// ── Snippet registry for the expression editor ───────────────────────────
// Plain data, keyed by slot kind, so a new kind (or a new snippet) is a data
// edit, not JSX. Kinds: "condition" (a one-line true/false expression) and
// "value_body" (a function body that returns the output value(s)).
//
// A snippet's `code` may contain placeholders written «like this». Inserting
// replaces each with its label text and selects the first one, so typing
// overwrites it. `wrap: true` means the first placeholder takes the editor's
// current selection when there is one (e.g. NOT / parentheses / round).
//
// Everything here must agree with the helpers the engine provides
// (code_expr/prelude.js): numeric comparisons go through num(), blank checks
// through isBlank(), text comparisons through strip(text(...)) so null,
// numbers and padding behave like the dropdown builder.

export const SNIPPET_CATEGORIES = {
  condition: [
    { id: 'all', label: 'All' },
    { id: 'math', label: 'Math' },
    { id: 'text', label: 'Text' },
    { id: 'logic', label: 'Logic' },
    { id: 'checks', label: 'Null/Number checks' },
    { id: 'conversion', label: 'Conversion' },
    { id: 'date', label: 'Date' },
  ],
  value_body: [
    { id: 'all', label: 'All' },
    { id: 'ifelse', label: 'If/Else' },
    { id: 'return', label: 'Return' },
    { id: 'logic', label: 'Logic' },
    { id: 'text', label: 'Text' },
    { id: 'math', label: 'Math' },
    { id: 'checks', label: 'Null/Number checks' },
    { id: 'conversion', label: 'Conversion' },
    { id: 'date', label: 'Date' },
  ],
};

export const SNIPPETS = {
  condition: [
    // ── Math ──
    { id: 'gt', category: 'math', label: 'Greater than', code: 'num(row.«column») > «number»', example: 'num(row.amount) > 100 — rows where amount is above 100' },
    { id: 'gte', category: 'math', label: 'At least', code: 'num(row.«column») >= «number»', example: 'num(row.amount) >= 100' },
    { id: 'lt', category: 'math', label: 'Less than', code: 'num(row.«column») < «number»', example: 'num(row.age) < 18' },
    { id: 'lte', category: 'math', label: 'At most', code: 'num(row.«column») <= «number»', example: 'num(row.age) <= 65' },
    { id: 'eqnum', category: 'math', label: 'Equals number', code: 'num(row.«column») === «number»', example: 'num(row.qty) === 0' },
    { id: 'between', category: 'math', label: 'Between', code: 'num(row.«column») >= «low» && num(row.«column») <= «high»', example: 'num(row.score) >= 50 && num(row.score) <= 80' },
    { id: 'colvscol', category: 'math', label: 'Column vs column', code: 'num(row.«column») > num(row.«other»)', example: 'num(row.actual) > num(row.budget)' },
    { id: 'add', category: 'math', label: 'Add', code: 'num(row.«a») + num(row.«b»)', example: 'num(row.price) + num(row.tax) > 100' },
    { id: 'sub', category: 'math', label: 'Subtract', code: 'num(row.«a») - num(row.«b»)', example: 'num(row.total) - num(row.paid) > 0' },
    { id: 'mul', category: 'math', label: 'Multiply', code: 'num(row.«a») * num(row.«b»)', example: 'num(row.qty) * num(row.price) > 1000' },
    { id: 'div', category: 'math', label: 'Divide', code: 'num(row.«a») / num(row.«b»)', example: 'num(row.cost) / num(row.qty) > 5' },
    { id: 'round', category: 'math', label: 'Round', code: 'Math.round(«number»)', wrap: true, example: 'Math.round(num(row.amount)) === 100' },
    { id: 'round-dp', category: 'math', label: 'Round to decimals', code: 'round(row.«column», 2)', example: 'round(row.price, 2) — 99.9878232 becomes 99.99; blank or not a number is null' },
    { id: 'abs', category: 'math', label: 'Absolute value', code: 'Math.abs(«number»)', wrap: true, example: 'Math.abs(num(row.diff)) > 5' },
    { id: 'even', category: 'math', label: 'Is even', code: 'num(row.«column») % 2 === 0', example: 'num(row.id) % 2 === 0' },

    // ── Text ──
    { id: 'eqtext', category: 'text', label: 'Equals text', code: 'strip(text(row.«column»)).toLowerCase() === "«value»"', example: 'strip(text(row.status)).toLowerCase() === "active"' },
    { id: 'neqtext', category: 'text', label: 'Does not equal text', code: 'strip(text(row.«column»)).toLowerCase() !== "«value»"', example: 'strip(text(row.status)).toLowerCase() !== "closed"' },
    { id: 'contains', category: 'text', label: 'Contains', code: 'text(row.«column»).toLowerCase().includes("«value»")', example: 'text(row.email).toLowerCase().includes("@acme")' },
    { id: 'startswith', category: 'text', label: 'Starts with', code: 'strip(text(row.«column»)).toLowerCase().startsWith("«value»")', example: 'strip(text(row.code)).toLowerCase().startsWith("us-")' },
    { id: 'endswith', category: 'text', label: 'Ends with', code: 'strip(text(row.«column»)).toLowerCase().endsWith("«value»")', example: 'strip(text(row.file)).toLowerCase().endsWith(".csv")' },
    { id: 'inlist', category: 'text', label: 'Is one of (text)', code: '["«a»", "«b»", "«c»"].includes(strip(text(row.«column»)))', example: '["NY", "LA", "SF"].includes(strip(text(row.city)))' },
    { id: 'len', category: 'text', label: 'Text length', code: 'strip(text(row.«column»)).length > «n»', example: 'strip(text(row.name)).length > 20' },
    { id: 'regex', category: 'text', label: 'Matches pattern', code: '/«pattern»/.test(text(row.«column»))', example: '/^[A-Z]{2}\\d+$/.test(text(row.sku))' },

    // ── Logic ──
    { id: 'and', category: 'logic', label: 'AND', code: '(«condition») && («condition2»)', wrap: true, example: '(num(row.age) >= 18) && (row.country === "US")' },
    { id: 'or', category: 'logic', label: 'OR', code: '(«condition») || («condition2»)', wrap: true, example: '(row.tier === "gold") || (num(row.spend) > 1000)' },
    { id: 'not', category: 'logic', label: 'NOT', code: '!(«condition»)', wrap: true, example: '!(isBlank(row.email))' },
    { id: 'group', category: 'logic', label: 'Group ( … )', code: '(«expression»)', wrap: true, example: '(a || b) && c' },
    { id: 'ternary', category: 'logic', label: 'If / then / else', code: '«condition» ? «yes» : «no»', wrap: true, example: 'row.tier === "gold" ? num(row.amt) > 100 : num(row.amt) > 500' },
    { id: 'innum', category: 'logic', label: 'Is one of (numbers)', code: '[«1», «2», «3»].includes(num(row.«column»))', example: '[1, 2, 3].includes(num(row.level))' },

    // ── Null / number checks ──
    { id: 'blank', category: 'checks', label: 'Is blank', code: 'isBlank(row.«column»)', example: 'isBlank(row.phone) — empty, spaces, "N/A" or "—"' },
    { id: 'notblank', category: 'checks', label: 'Is not blank', code: '!isBlank(row.«column»)', example: '!isBlank(row.phone)' },
    { id: 'isnum', category: 'checks', label: 'Is a number', code: 'num(row.«column») !== null', example: 'num(row.amount) !== null — "$1,234" counts' },
    { id: 'notnum', category: 'checks', label: 'Is not a number', code: 'num(row.«column») === null', example: 'num(row.amount) === null' },
    { id: 'positive', category: 'checks', label: 'Is a positive number', code: 'num(row.«column») !== null && num(row.«column») > 0', example: 'num(row.amount) !== null && num(row.amount) > 0' },
    { id: 'blankorzero', category: 'checks', label: 'Blank or zero', code: 'isBlank(row.«column») || num(row.«column») === 0', example: 'isBlank(row.qty) || num(row.qty) === 0' },

    // ── Conversion ──
    // Same blank-safe shape as everywhere else: unrecognized input is null,
    // never a guess — see code_expr/prelude.py for the exact contract.
    { id: 'to-number', category: 'conversion', label: 'To number', code: 'num(row.«column»)', example: 'num(row.amount) — "$1,234.50" becomes 1234.5; blank or unparseable is null' },
    { id: 'to-int', category: 'conversion', label: 'To whole number', code: 'toInt(row.«column»)', example: 'toInt(row.qty) — 12.9 becomes 12 (truncated toward zero); blank or unparseable is null' },
    { id: 'to-text', category: 'conversion', label: 'To text', code: 'text(row.«column»)', example: 'text(row.amount) — 1234.5 becomes "1234.5"; blank becomes ""' },
    { id: 'to-boolean', category: 'conversion', label: 'To true/false', code: 'toBoolean(row.«column»)', example: 'toBoolean(row.active) — "yes"/"true"/"y"/"t" or any nonzero number is true; "no"/"false"/"n"/"f" or zero is false; blank or unrecognized is null' },
    { id: 'to-date', category: 'conversion', label: 'To date', code: 'toDate(row.«column»)', example: 'toDate(row.order_date) — reads YYYY-MM-DD, ISO datetimes, or 8-digit dates; anything else is null' },
  ],
};


// ── Date snippets ──
// Calendar dates only. None of them inserts an INPUT format on its own: the
// plain forms read a column that is already ISO text (or year-first), and
// "Parse with a format" leaves the format string empty for the user to fill
// in (the hover examples show the usual ones). The date detector in the
// column list is how a format gets chosen.
export const DATE_SNIPPETS = [
  { id: 'date-add-days', category: 'date', label: 'Add days', code: 'dateAdd(row.«column», «number», "day")', example: 'dateAdd(row.order_date, 30, "day") — 30 days after the date' },
  { id: 'date-add-months', category: 'date', label: 'Add months', code: 'dateAdd(row.«column», «number», "month")', example: 'dateAdd(row.start, 3, "month") — Jan 31 + 1 month is the end of February' },
  { id: 'date-diff-days', category: 'date', label: 'Difference in days', code: 'dateDiff(row.«column», row.«other», "day")', example: 'dateDiff(row.start, row.end, "day") — negative when end is before start' },
  { id: 'date-diff-months', category: 'date', label: 'Difference in months', code: 'dateDiff(row.«column», row.«other», "month")', example: 'dateDiff(row.start, row.end, "month") — whole months' },
  { id: 'date-year', category: 'date', label: 'Year', code: 'yearOf(row.«column»)', example: 'yearOf(row.order_date) — 2024' },
  { id: 'date-month', category: 'date', label: 'Month number', code: 'monthOf(row.«column»)', example: 'monthOf(row.order_date) — 1 to 12' },
  { id: 'date-day', category: 'date', label: 'Day of month', code: 'dayOf(row.«column»)', example: 'dayOf(row.order_date) — 1 to 31' },
  { id: 'date-quarter', category: 'date', label: 'Quarter', code: 'quarterOf(row.«column»)', example: 'quarterOf(row.order_date) — 1 to 4' },
  { id: 'date-weekday', category: 'date', label: 'Weekday', code: 'weekdayOf(row.«column»)', example: 'weekdayOf(row.order_date) — 1 = Monday ... 7 = Sunday' },
  { id: 'date-weekend', category: 'date', label: 'Is weekend', code: 'weekdayOf(row.«column») >= 6', example: 'weekdayOf(row.order_date) >= 6 — Saturday or Sunday' },
  { id: 'date-som', category: 'date', label: 'Start of month', code: 'startOfMonth(row.«column»)', example: 'startOfMonth(row.order_date) — 2024-01-01' },
  { id: 'date-eom', category: 'date', label: 'End of month', code: 'endOfMonth(row.«column»)', example: 'endOfMonth(row.order_date) — 2024-01-31' },
  { id: 'date-format-mon-year', category: 'date', label: 'Format as "MMM YYYY"', code: 'formatDate(row.«column», "MMM YYYY")', example: 'formatDate(row.order_date, "MMM YYYY") — Jan 2024' },
  { id: 'date-valid', category: 'date', label: 'Is a valid date', code: 'isDate(row.«column»)', example: 'isDate(row.order_date) — false for blanks and impossible dates' },
  { id: 'date-before-today', category: 'date', label: 'Before today', code: 'toDate(row.«column») !== null && toDate(row.«column») < today()', example: 'toDate(row.due) !== null && toDate(row.due) < today() — overdue' },
  { id: 'date-after-today', category: 'date', label: 'After today', code: 'toDate(row.«column») !== null && toDate(row.«column») > today()', example: 'toDate(row.due) !== null && toDate(row.due) > today() — still to come' },
];
// Statement form (function body only): the user types the format.
export const DATE_PARSE_SNIPPET = {
  id: 'date-parse-format', category: 'date', label: 'Parse with a format',
  code: 'const «date_var» = toDate(row.«column», "«format»");',
  focus: 'format',                                      // the user's next step: the caret goes into the quotes
  example: 'const d = toDate(row.order_date, "DD/MM/YYYY") — India / UK. Use "MM/DD/YYYY" for the US, "YYYY/MM/DD" for China, Japan, Korea.',
};

// ── value_body: statements that decide what a row returns ──
// «outputs» in a code string is replaced with the block's output names as an
// object literal (see expandSnippet), so "return object" is always pre-filled.
const BODY_ONLY = [
  // If / else
  { id: 'if', category: 'ifelse', label: 'if', code: 'if («condition») {\n  return «value»;\n}', example: 'if (num(row.amount) > 100) { return "big"; }' },
  { id: 'ifelse', category: 'ifelse', label: 'if / else', code: 'if («condition») {\n  return «value»;\n} else {\n  return «other»;\n}', example: 'if (isBlank(row.email)) { return "no"; } else { return "yes"; }' },
  { id: 'elseif', category: 'ifelse', label: 'else-if chain', code: 'if («condition») {\n  return «value»;\n} else if («condition2») {\n  return «value2»;\n} else {\n  return «other»;\n}', example: 'if (n > 100) { return "high"; } else if (n > 10) { return "mid"; } else { return "low"; }' },
  { id: 'nested', category: 'ifelse', label: 'nested if', code: 'if («condition») {\n  if («inner») {\n    return «value»;\n  }\n  return «other»;\n}', example: 'if (row.region === "EU") { if (n > 5) { return "a"; } return "b"; }' },
  { id: 'ternary-return', category: 'ifelse', label: 'ternary', code: 'return «condition» ? «value» : «other»;', example: 'return num(row.qty) > 0 ? "in stock" : "out";' },
  { id: 'const', category: 'logic', label: 'const', code: 'const «name» = «value»;', example: 'const total = num(row.price) * num(row.qty);' },
  // Return
  { id: 'return-value', category: 'return', label: 'return value', code: 'return «value»;', example: 'return "gold";' },
  { id: 'return-object', category: 'return', label: 'return object (output names)', code: 'return «outputs»;', example: 'return { tier: "gold", score: 10 };', usesOutputs: true },
  { id: 'return-keep', category: 'return', label: 'return the column\u2019s value', code: 'return row.«column»;', example: 'return row.status; — keeps the existing value' },
  { id: 'return-blank', category: 'return', label: 'return blank', code: 'return "";', example: 'return "";' },
  { id: 'return-early', category: 'return', label: 'early return', code: 'if («condition») {\n  return «value»;\n}', example: 'if (isBlank(row.id)) { return ""; }' },
];
SNIPPETS.condition.push(...DATE_SNIPPETS.map(s => ({ ...s })));
SNIPPETS.value_body = [
  ...BODY_ONLY,
  ...SNIPPETS.condition.map(s => ({ ...s })),
  { ...DATE_PARSE_SNIPPET },
];

// Replace «outputs» with the declared outputs as an object literal (each value
// a «value» placeholder). Snippets without it are returned unchanged.
export function expandSnippet(snippet, outputs = []) {
  if (!snippet.usesOutputs) return snippet;
  const names = (outputs || []).filter(n => String(n).trim() !== '');
  const body = names.length
    ? '{\n' + names.map(n => `  ${objectKey(n)}: «value»`).join(',\n') + '\n}'
    : '{}';
  return { ...snippet, code: snippet.code.replace('«outputs»', body) };
}

// Insert at the cursor keeping the current line's indentation on every line
// the snippet adds.
export function applySnippetIndented(text, selStart, selEnd, snippet, ctx = {}) {
  const indent = lineIndent(text, Math.max(0, Math.min(selStart, text.length)));
  const indented = indent ? { ...snippet, code: snippet.code.replace(/\n/g, '\n' + indent) } : snippet;
  return applySnippet(text, selStart, selEnd, indented, ctx);
}

export function snippetsFor(kind, category = 'all', query = '') {
  const all = SNIPPETS[kind] || [];
  const q = String(query || '').trim().toLowerCase();
  return all.filter(s =>
    (category === 'all' || s.category === category)
    && (!q || s.label.toLowerCase().includes(q) || s.code.toLowerCase().includes(q)));
}

const PLACEHOLDER = /«([^»]*)»/g;

// Every placeholder is replaced with VALID, HARMLESS code so an unedited
// insert still parses and runs: conditions are true, values are plain text,
// numbers are 0, a column is a real input column. No marker characters stay.
const DEFAULTS = {
  condition: 'true', condition2: 'true', inner: 'true', expression: 'true',
  yes: 'true', no: 'false',
  value: '"value"', value2: '"value2"', other: '"other"', then: '"value"', else: '"other"',
  number: '0', low: '0', high: '10', n: '0',
  pattern: '.+',
};
const COLUMN_TOKENS = { column: 0, a: 0, b: 1, other: 1 };      // -> index into the input columns

export function uniqueName(text, base = 'value') {
  const taken = new Set([...String(text).matchAll(/\b(?:const|let|var)\s+([A-Za-z_$][\w$]*)/g)].map(m => m[1]));
  if (!taken.has(base)) return base;
  let i = 2;
  while (taken.has(base + i)) i += 1;
  return base + i;
}

// What one placeholder becomes. `ctx.columns` = real input column names.
function fillPlaceholder(label, { inQuotes, asColumn, columns, text }) {
  if (asColumn) {
    const idx = COLUMN_TOKENS[label] ?? 0;
    const cols = columns || [];
    return columnRef(cols[idx] ?? cols[0] ?? 'column');
  }
  if (inQuotes) return '';                                       // a text literal: ""
  if (label === 'name') return uniqueName(text);
  if (label === 'date_var') return uniqueName(text, 'd');
  if (/^\d+$/.test(label)) return label;                        // a numbered slot keeps its digit
  return DEFAULTS[label] ?? '0';
}

// Insert `snippet` into `text`, replacing [selStart, selEnd). Returns the new
// text, the range to select (the first placeholder's default text, else a
// caret after the inserted code) and the inserted range [hlStart, hlEnd).
// `ctx.columns` makes column placeholders real columns.
export function applySnippet(text, selStart, selEnd, snippet, ctx = {}) {
  const start = Math.max(0, Math.min(selStart, text.length));
  const end = Math.max(start, Math.min(selEnd, text.length));
  const selected = text.slice(start, end);
  const useSelection = !!snippet.wrap && selected !== '';
  const rest = text.slice(0, start) + text.slice(end);

  // `row.«column»` stands for a whole reference: row.x or row["x y"].
  const code = snippet.code.replace(/row\.«(column|other|a|b)»/g, (_m, k) => `«@${k}»`);
  let out = '';
  let firstSel = null;       // [from, to) in `out`
  let focusSel = null;       // a snippet may name the placeholder to select instead
  let consumedWrap = false;
  let last = 0;
  for (const m of code.matchAll(PLACEHOLDER)) {
    out += code.slice(last, m.index);
    last = m.index + m[0].length;
    if (useSelection && !consumedWrap) {
      out += selected;
      consumedWrap = true;
      continue;
    }
    const asColumn = m[1].startsWith('@');
    const label = asColumn ? m[1].slice(1) : m[1];
    const inQuotes = code[m.index - 1] === '"' && code[last] === '"';
    const from = out.length;
    out += fillPlaceholder(label, { inQuotes, asColumn, columns: ctx.columns, text: rest });
    if (firstSel === null) firstSel = [from, out.length];
    if (snippet.focus && label === snippet.focus && focusSel === null) focusSel = [from, out.length];
  }
  out += code.slice(last);

  const next = text.slice(0, start) + out + text.slice(end);
  const hl = { hlStart: start, hlEnd: start + out.length };
  if (focusSel) firstSel = focusSel;
  if (firstSel) return { text: next, selStart: start + firstSel[0], selEnd: start + firstSel[1], ...hl };
  return { text: next, selStart: start + out.length, selEnd: start + out.length, ...hl };
}
